import React from 'react';
import { Footnote, FootnoteRef, FootNoteRefProps_t } from '../components/FootNote';
import { BlockMath, InlineMath } from 'react-katex';
import InvertedIndex from '../assets/inverted_index.svg';

export const TrueSearcherPage: React.FC<{}> = () => {

    // These footnotes are a little hacky. If you want your footnote to be in order,
    // you have to add it to this list in the order you want it.
    const references: Array<FootNoteRefProps_t> = [
        { id: "code red Google", ref: "https://www.nytimes.com/2022/12/21/technology/ai-chatgpt-google-search.html" },
        { id: "boolean", text: "page 15", ref: "https://nlp.stanford.edu/IR-book/pdf/01bool.pdf" },
        { id: "bm25", ref: "https://emschwartz.me/understanding-the-bm25-full-text-search-algorithm/" },
        { id: "intro to information", ref: "https://nlp.stanford.edu/IR-book/information-retrieval-book.html" },
        { id: "elmo", ref: "https://arxiv.org/pdf/1802.05365" },
        { id: "broken search", ref: "https://www.google.com/search?q=article+on+argentina+not+from+new+york+times" },
        { id: "milvus", ref: "https://github.com/milvus-io/milvus" },
        { id: "pinecone", ref: "https://github.com/pinecone-io" },
        { id: "weaviate", ref: "https://github.com/weaviate/weaviate" },
        { id: "turbopuffer", ref: "https://turbopuffer.com/" },
        { id: 'longcite', ref: "https://arxiv.org/pdf/2409.02897" },
        { id: 'selfcite', ref: "https://arxiv.org/pdf/2502.09604" },
        { id: "1m", ref: "https://claude.com/blog/1m-context-ga" },
        { id: "miller", ref: "https://yusufarslan.net/sites/yusufarslan.net/files/upload/content/Miller1968.pdf" },
        { id: "Nielsen", ref: "https://www.sciencedirect.com/book/monograph/9780125184069/usability-engineering" },
        { id: "3 limits", ref: "https://zurb.com/blog/response-times-the-3-important-limits" },
        { id: "Google slow", ref: "https://static.googleusercontent.com/media/research.google.com/en//pubs/archive/34439.pdf" },
        { id: "Google usage speed", ref: "https://services.google.com/fh/files/blogs/google_delayexp.pdf" },
    ];

    // This gets the reference number that we render next to the footnote.
    // It just finds the index of the footnote with the given id in the list.
    const f = React.useCallback((id: string) => {
        let index = references.findIndex(ref => ref.id === id);
        if (index == -1) {
            throw Error(`could not find reference: ${id} in list`);
        }
        return index + 1;
    }, [references]);

    return (
        <main>
            <h1>True Searcher: Dynamic Agentic Query Generation</h1>
            <p>
                I've been working on a search product for almost 2 years now and I'd like to tell you about it.
            </p>
            <h4>Big Picture Trends</h4>
            <p>
                A few years ago I noticed a few big trends
                <ol>
                    <li>
                        Google Search was getting much worse. Results were less relevant, results were covered with ads, and results were filled with
                        AI, SEO, & human slop. Try searching for a recipe.
                    </li>
                    <li>
                        The regulatory environment was shifting towards a more anti-trust stance.
                    </li>
                    <li>
                        Large Language Models (LLMs) and products that use them like ChatGPT were upending how people search the web.
                        ChatGPT's launch caused some inside Google to declare "code red"<Footnote id="code red Google" f={f} />.
                    </li>
                </ol>
            </p>
            <p>
                So far Google seems to have avoided the most drastic anti-trust remedies but the other two trends have continued. Search
                results used to mean a list of blue links. Now a ChatGPT response is a detailed explanation to a specific question.
            </p>
            <h4>How Search Used to Work: Filters</h4>
            <p>
                Imagine if we had billions of documents and we wanted to find all that contained the words "Rome" and "cat".
                Scanning through all of them would take too long.
            </p>
            <p>
                Instead, text search systems used a data structure called an <em>inverted index</em>.
                It's inverted because a normal index maps from document_id → document_text.
                At a high level, an inverted index maps from document_text → document_id.
            </p>
            <p>
                When we construct the index we have to scan through all of the documents, but we never have to scan through them all at query time.
            </p>
            <p>The index is composed of a <em>term dictionary</em> that maps from words to <em>posting lists</em>. Posting lists are lists of document IDs in sorted order.</p>
            <p>The posting list for a term contains the document IDs of all of the documents that contain the term.</p>

            <div style={{ textAlign: 'center' }}>
                <img src={InvertedIndex} alt="Basic Inverted Index" style={{ width: '50%' }} />
            </div>
            <p>
                To find all documents that contain the words "when" and "Rome" we find the intersection of the posting list for "when" [2, 108]
                and "Rome" [2, 4, 108] which gives us the documents [2, 108]. Neat!
            </p>
            <p>
                To find all documents that contain the words "in" or "cat"
                we find the union of the posting list for "in" and "cat".
            </p>
            <p>
                Note that inverted indexes can be used for filtering other fields: year of publication, author name etc.
            </p>
            <p>
                These are called Boolean queries and one of the first search engines WestLaw (started in 1975 !), used
                them to look up legal cases. But the "problem with Boolean search is that using
                AND operators tends to produce high precision but low recall searches, while using OR operators gives low precision but high
                recall searches, and it is difficult or impossible to find a satisfactory middle ground."<Footnote id="boolean" f={f} />
            </p>
            <p>
                So Westlaw introduced what they call “Natural Language” queries. With these
                queries, the search engine has more freedom in how it interprets the user's query. It may ignore common words like "the" and
                search for variations of the same word ("run", "ran", and "running" etc.).
            </p>
            <p>
                This leads us to the very important idea of ranking. A search is likely to return
                far more documents than the user can hope to read. How do we rank the documents by relevance?
            </p>
            <h4>Text & Metadata Ranking</h4>
            <p>
                Algorithms like BM25 and tf-idf<Footnote id="bm25" f={f} /> rank documents based on the text. At a high level these algorithms examine the frequency of
                query terms in the documents and compare them to the frequency of the query terms in all documents. For example if you search for "experiments on
                relativistic velocity addition", the word "on" will appear in many documents. So it is probably not very important for your query. But the term "relativistic"
                is rare, so documents that contain it are more likely to be relevant to your query.
            </p>
            <p>
                There's variations of this where we look for bigrams (e.g. "experiments on", "on relativistic", "relativistic velocity", "velocity addition") or
                ignore common words (e.g. "on", "the").
            </p>
            <p>If the query is related to news, we may prefer documents that were
                published more recently. We can also filter and rank by author or any other metadata.</p>
            <p>
                Should we split hyphenated words? How do we handle typos in documents? Language is tricky and there could be an
                entire book about some of these techniques. In fact there is. I would
                suggest reading chapters 1-9, 19 and 21 of Introduction to Information Retrieval<Footnote id="intro to information" f={f} />.
            </p>
            <h4>Vector Search</h4>
            <p>
                But writing down a rule for every case may be intractable. For example
                consider the query "article about apple before cook". The word "apple" here refers to the technology company and not the
                fruit but we only know that because
                of the presence of the word "cook". We know that "cook" refers
                to the person Tim Cook and not the profession because he is associated with the company Apple.
            </p>
            <p>
                These words do not appear adjacent to each other
                so we can't use bigrams. The meaning also relies on context not in the query itself.
            </p>
            <p>
                So instead of writing rules, we train a machine learning model to create word <em>embeddings</em>.
                These convert a word to a vector in a vector space where words with similar meanings have close vectors.
                That particular technique where the nearby words help inform us about the meaning of this word ("apple" the company not the fruit)
                was introduced in the ELMo paper<Footnote id="elmo" f={f} />.
            </p>
            <p>
                There's one more trick. Rather than embedding single words, we embed many words. This allows us to embed a whole document.
            </p>
            <p>
                So at indexing time we calculate the embeddings for all of the documents in our index and store the embeddings (just vectors of floating point numbers) in a database. 
                At query time, when we receive a query we calculate the embedding for the query and find the document with the closest embedding to the query embedding.
                This technique works amazingly well and is called semantic search or <em>vector search</em>.
            </p>
            <h4>Vector Search Problems</h4>
            <p>
                Imagine if I search for "article on argentina not from new york times". It is likely that the closest vector
                to that query mentions the New York Times and Argentina. When I search this on Google, 7/10 of the top
                results are from the New York Times.<Footnote id="broken search" f={f} />
            </p>
            <p>
                This is where the logical reasoning of language clashes with the fuzzy search of vector search.
            </p>
            <h4>Hybrid Search</h4>
            <p>
                In the "article on argentina not from new york times" example, I'd like to filter out nytimes.com from my results and then
                find the most semantically similar document to "article on argentina". This combination of vector search with filters is called <em>hybrid search</em>.
            </p>
            <p>
                Performing these hybrid queries efficiently is actually surprisingly difficult. The filters must be applied as the vector
                search is happening. Not before or after. Luckily in the last few years there has been an explosion of vector databases with
                support for hybrid queries.<Footnote id="milvus" f={f} /> <Footnote id="pinecone" f={f} /> <Footnote id="weaviate" f={f} /> <Footnote id="turbopuffer" f={f} />
            </p>
            <p>
                But how do we know what filters to apply?
            </p>
            <h4>Which Filters?</h4>
            <p>
                One problem with choosing filters is that sometimes our data is imperfect or we haven't indexed the field we'd like to filter on. Imagine if our
                query was "paper on vector steering from European author". We might not have the nationality of every author indexed. And even if we did,
                we still would need to know which countries are in Europe to know which nations to filter to.
            </p>
            <p>
                The query may also be ambiguous. For the "article about apple before cook" does the query mean before
                Cook was CEO (2011) or before he started working at the company (1998)?
            </p>
            <p>
                Additionally some filters may be vague "old-fashioned recipe for chocolate mousse". What year counts as old-fashioned? And what if we filter too far back and
                find no results?
            </p>
            <h4>True Searcher Part 1: An AI Translator</h4>
            <p>
                By prompting an LLM with the user's query and the data sources available, it can construct the query plan needed to find relevant documents. The filters are displayed to the user
                so they can check them and adjust them if needed.
            </p>
            <p>
                We also prompt the LLM to double check with the user about filters it is uncertain about. This is a feature that previous search engines before LLMs never could
                have implemented. This back and forth removes a lot of guesswork out of constructing query plans.
            </p>
            <h4>True Searcher Part 2: Dynamic Agentic Query Generation</h4>
            <p>
                In a database management system like Postgres, the query planner will try to estimate the cost of operations. That entails estimating of the sizes
                of intermediate results. If a result is expected to be large, the planner may choose a different plan than if it is small. Some planners will adaptively change the plan based on
                intermediate result sizes as they are computed.
            </p>
            <p>
                When the True Searcher query plan is constructed, we rank the filters in importance and fuzziness. For example if we have a non-nullable field on the
                year and the query contains a specific year to filter on, that's a very exact filter. A filter like
                "author grew up in Wisconsin" is much more fuzzy. We likely haven't indexed that. So we apply filters in decreasing order of fuzziness and see how large the results are. If we have
                a small number of results, we can send all of them to the LLM and ask it to apply the more fuzzy filters.
            </p>
            <p>
                For example for the query "paper on vector steering from European author", there aren't that many papers on vector steering. We can just give the LLM the names and emails of the authors and it 
                can narrow it down.
            </p>
            <h4>True Searcher Part 3: Careful Indexing for Fuzzy Searching</h4>
            <p>
                When we index documents, it's important to gather as many structured fields as we can. Can we parse the date from the document? Can we parse the author from the document? Every bit of information that a user might filter on,
                we'd like to index. Writing an extremely robust parser for every kind of document is a large task. Fortunately we don't have to do that. We can use an ensemble of LLMs combined with some heuristics to
                be fairly confident we have parsed the document well.
            </p>
            <p>
                We also can use the LLMs to answer more fuzzy questions "what tags apply to this document?". If it's a recipe we can ask "what allergens are in this recipe?".
            </p>
            <p>
                We also restrict ourselves to trusted sources. It is surprising how much content there is entirely on .edu domains. Recipes, travel guides, legal advice, you name it. These web pages tend to be
                simpler, more accurate, smaller (no javascript), easier to parse, and have more permissive robots.txt rules.
            </p>
            <p>
                Finally we parse different kinds of documents uniquely and put them into their own table. So academic papers are separated from recipes and they have different schemas. Authors and citations matter more
                for papers whereas allergens and ingredients matter more for recipes.
            </p>
            <h4>True Seacher Part 4: Joins</h4>
            <p>
                Sometimes a query requires joining data from multiple results: "find all papers with more than 100 citations from the authors of the attention is all you need paper".
            </p>
            <p>
                This has a lot of edge cases that make it more difficult but feels like science fiction when it works.
            </p>
            <h4>Technological Trends</h4>
            <p>
                There's a few trends that have made this possible:
            </p>
            <ol>
                <li>
                    <strong>Hybrid Search is Feasible Now</strong>
                    <p>
                        Hybrid semantic vector search and attribute search database technology has improved dramatically.
                    </p>
                </li>
                <li>
                    <strong>LLMs Can Now Translate English → SQL </strong>
                    <p>
                        Given a description of what you are looking for and the schemas of the available tables,
                        an LLM can create a relevant query extremely reliably. Additionally most search queries result in quite simple SQL.
                    </p>
                </li>
                <li>
                    <strong>We Can Now Force LLMs to Use Citations</strong>
                    <p>
                        Training techniques to force LLMs to provide citations for claims like LongCite<Footnote id='longcite' f={f} /> and SelfCite<Footnote id='selfcite' f={f} />  have made LLMs much more reliable when
                        provided relevant documents in their context. Additionally the maximum length of context that can be
                        provided to LLMs (the <em>context window</em>), has grown massively to as high as 1 million tokens.<Footnote id="1m" f={f} />
                    </p>
                </li>
            </ol>
            <h4>Product Trends</h4>
            <p>

            </p>
            <ol>
                <li>
                    <strong>Users are Willing to Wait for Better Results</strong>
                    <p>
                        For UI latency, 100ms is the limit for it feeling instant and 1s is the limit before a user's flow
                        gets interrupted<Footnote id="miller" f={f} /> <Footnote id="Nielsen" f={f} /> <Footnote id="3 limits" f={f} />. Google's internal research has characterized 250ms as
                        fast and 2s as slow<Footnote id="Google slow" f={f} /> and that small increases in latency decrease
                        search usage<Footnote id="Google usage speed" f={f} />.
                    </p>
                    <p>
                        Early chatbots were much slower than 250ms to produce their first token and modern thinking models can take many seconds. That slowness has not kept
                        users away! I think users are willing to wait to get a high-quality result.
                    </p>
                    <p>
                        This larger latency budget allows us to perform more complicated queries.
                    </p>
                </li>
                <li>
                    <strong>Users are Willing to Specify What They Want</strong>
                    <p>
                        Westlaw boolean queries required users to specify what filters they wanted to apply in an esoteric search language.
                        To find relevant documents, a user had to tediously adjust these filters. I think few have the patience for that now.
                    </p>
                    <p>
                        Users are willing to engage in a back and forth with AI chatbots to specify exactly what they want. Language as a
                        user interface allows incredibly accessible and precise specification of user preferences.
                    </p>
                </li>
            </ol>

            <h4>What's in the name?</h4>
            <p> <a href="https://truesearcher.com">truesearcher.com</a> was available for $1 so I went for it.</p>
            <h4>Feedback</h4>
            <p>
                Search is a fascinating combination of distributed systems, machine learning, databases, and user experience research.
                It's difficult to define what the right answer should be for a search result. When there are more documents in your index than you could ever hope to read, how do you know which should rank 1st?
            </p>
            <p>
                I'm seeking anyone interested in providing feedback in the coming months. <a href="https://www.truesearcher.com/#/signup">Sign up here.</a>
            </p>
            <p>Thanks for reading! :)</p>
            <section id="footnotes">
                {references.map((item, i) => (
                    <FootnoteRef id={item.id} text={item.text} ref={item.ref} number={i + 1} />
                ))
                }
            </section>
        </main>
    )
};
